import { useCallback, useEffect, useRef, useState } from "react";
import type { SpinningWheelUIReward } from "@cede/types";
import {
  ANIMATION_DURATIONS,
  WHEEL_MECHANICS,
  WHEEL_SPIN_PHASES,
  type WheelSpinPhase,
} from "@/constants/spinningWheel";

interface UseWheelAnimationProps {
  rewards: SpinningWheelUIReward[];
  onAnimationComplete?: (results: SpinningWheelUIReward[]) => void;
}

interface PendingResult {
  reward: SpinningWheelUIReward;
  rewardIndex: number;
  allResults: SpinningWheelUIReward[];
}

// Animation phase configurations
const INITIAL_SPIN_CONFIG = {
  duration: 1000,
  rotationsPerSecond: 3,
} as const;

const WAITING_SPIN_CONFIG = {
  degreesPerFrame: 12, // At 60fps, this is 1080 degrees per second (3 rotations/sec) - matches initial spin
} as const;

/**
 * Hook for managing the spinning wheel animation phases and rotation
 * Optimized to use requestAnimationFrame for smooth, performant animations
 */
export const useWheelAnimation = ({ rewards, onAnimationComplete }: UseWheelAnimationProps) => {
  const [spinPhase, setSpinPhase] = useState<WheelSpinPhase>(WHEEL_SPIN_PHASES.IDLE);
  const [currentRotation, setCurrentRotation] = useState<number>(0);
  const [winningResult, setWinningResult] = useState<SpinningWheelUIReward | null>(null);

  // Use ref to track rotation for smooth animation without causing re-renders
  const rotationRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const animationStartTimeRef = useRef<number>(0);
  const pendingResult = useRef<PendingResult | null>(null);
  const resultsToShow = useRef<SpinningWheelUIReward[]>([]);
  const landingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resultTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Clean up all animation resources
   */
  const cleanupAnimations = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (landingTimeoutRef.current !== null) {
      clearTimeout(landingTimeoutRef.current);
      landingTimeoutRef.current = null;
    }
    if (resultTimeoutRef.current !== null) {
      clearTimeout(resultTimeoutRef.current);
      resultTimeoutRef.current = null;
    }
  }, []);

  /**
   * Phase 3: Landing animation to winning position
   */
  const startLandingAnimation = useCallback(
    (targetReward: SpinningWheelUIReward, providedRewardIndex?: number, allResults?: SpinningWheelUIReward[]) => {
      // Stop any ongoing animations
      cleanupAnimations();

      setSpinPhase(WHEEL_SPIN_PHASES.LANDING);

      // Store results to show after animation
      if (allResults) {
        resultsToShow.current = allResults;
      }

      const rewardIndex =
        providedRewardIndex !== undefined ? providedRewardIndex : rewards.findIndex((r) => r.id === targetReward.id);

      // Helper to finalize animation
      const finalizeAnimation = () => {
        const resultsToPass = resultsToShow.current.length > 0 ? resultsToShow.current : [targetReward];
        onAnimationComplete?.(resultsToPass);
        resultsToShow.current = [];
      };

      if (rewardIndex === -1) {
        landingTimeoutRef.current = setTimeout(() => {
          setSpinPhase(WHEEL_SPIN_PHASES.IDLE);
          finalizeAnimation();
        }, ANIMATION_DURATIONS.LANDING);
        return;
      }

      // Calculate final rotation with random offset for natural feel
      const segmentSize = 360 / rewards.length;
      const randomOffset = (Math.random() - 0.5) * segmentSize * WHEEL_MECHANICS.SEGMENT_RANDOM_OFFSET;
      const finalRotation =
        WHEEL_MECHANICS.FULL_ROTATIONS * 360 +
        (-360 * rewardIndex) / rewards.length +
        WHEEL_MECHANICS.TOP_ARROW_ADJUSTMENT +
        randomOffset;

      rotationRef.current = finalRotation;
      setCurrentRotation(finalRotation);

      // Schedule winning result reveal and completion
      landingTimeoutRef.current = setTimeout(() => {
        setWinningResult(targetReward);
        setSpinPhase(WHEEL_SPIN_PHASES.IDLE);

        // Show results after a small delay for better UX
        resultTimeoutRef.current = setTimeout(() => {
          finalizeAnimation();
        }, ANIMATION_DURATIONS.RESULT_DELAY);
      }, ANIMATION_DURATIONS.LANDING);
    },
    [rewards, onAnimationComplete, cleanupAnimations],
  );

  /**
   * Waiting spin animation loop
   * Phase 2: Moderate spin while waiting for SSE result
   */
  const animateWaitingSpin = useCallback(() => {
    rotationRef.current = (rotationRef.current + WAITING_SPIN_CONFIG.degreesPerFrame) % 360;
    setCurrentRotation(rotationRef.current);

    // Continue animation loop - will be stopped by cleanupAnimations
    animationFrameRef.current = requestAnimationFrame(animateWaitingSpin);
  }, []);

  /**
   * Main animation loop using requestAnimationFrame
   * Handles both initial spin and waiting spin phases
   */
  const animateRotation = useCallback(
    (timestamp: number) => {
      const elapsed = timestamp - animationStartTimeRef.current;
      const { duration, rotationsPerSecond } = INITIAL_SPIN_CONFIG;

      if (elapsed < duration) {
        // Phase 1: Initial fast spin
        const degreesPerSecond = rotationsPerSecond * 360;
        const rotation = (elapsed / 1000) * degreesPerSecond;
        rotationRef.current = rotation;
        setCurrentRotation(rotation);

        animationFrameRef.current = requestAnimationFrame(animateRotation);
      } else {
        // Transition to waiting phase
        setSpinPhase(WHEEL_SPIN_PHASES.WAITING_FOR_RESULT);
        animationStartTimeRef.current = 0; // Mark transition complete

        // Check for pending result
        if (pendingResult.current) {
          const { reward, rewardIndex, allResults } = pendingResult.current;
          pendingResult.current = null;
          startLandingAnimation(reward, rewardIndex, allResults);
        } else {
          // Start waiting spin
          animationFrameRef.current = requestAnimationFrame(animateWaitingSpin);
        }
      }
    },
    [startLandingAnimation, animateWaitingSpin],
  );

  /**
   * Phase 1: Start initial fast spin (3 rotations per second for 1 second)
   */
  const startInitialSpin = useCallback(() => {
    cleanupAnimations();

    setSpinPhase(WHEEL_SPIN_PHASES.INITIAL_SPIN);
    setCurrentRotation(0);
    setWinningResult(null);
    rotationRef.current = 0;
    animationStartTimeRef.current = performance.now();

    animationFrameRef.current = requestAnimationFrame(animateRotation);
  }, [animateRotation, cleanupAnimations]);

  /**
   * Store result to be processed after initial spin completes
   */
  const setPendingResult = useCallback(
    (reward: SpinningWheelUIReward, rewardIndex: number, allResults: SpinningWheelUIReward[]) => {
      // If we're already in waiting phase, immediately start landing animation
      if (animationStartTimeRef.current === 0) {
        cleanupAnimations();
        pendingResult.current = null;
        startLandingAnimation(reward, rewardIndex, allResults);
      } else {
        // Store for later processing when initial spin completes
        pendingResult.current = { reward, rewardIndex, allResults };
      }
    },
    [cleanupAnimations, startLandingAnimation],
  );

  /**
   * Reset animation state
   */
  const resetAnimation = useCallback(() => {
    cleanupAnimations();
    setSpinPhase(WHEEL_SPIN_PHASES.IDLE);
    setCurrentRotation(0);
    setWinningResult(null);
    rotationRef.current = 0;
    animationStartTimeRef.current = 0;
    pendingResult.current = null;
    resultsToShow.current = [];
  }, [cleanupAnimations]);

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      cleanupAnimations();
    };
  }, [cleanupAnimations]);

  return {
    spinPhase,
    currentRotation,
    winningResult,
    startInitialSpin,
    startLandingAnimation,
    setPendingResult,
    resetAnimation,
    cleanupAnimations,
    isSpinning: spinPhase !== WHEEL_SPIN_PHASES.IDLE && spinPhase !== WHEEL_SPIN_PHASES.SHOWING_RESULTS,
    isLanding: spinPhase === WHEEL_SPIN_PHASES.LANDING,
  };
};
